import React, { useCallback, useReducer } from "react";

import {
  Dialog,
  DialogContent,
  IconButton,
  Stack,
  SvgIcon,
} from "@mui/material";
import { Button, ButtonProps, Divider, Typography } from "components";
import {
  AlertCircleIcon,
  AlertTriangleIcon,
  CheckCircleIcon,
  InfoCircleIcon,
  XIcon,
} from "components/Icon";
import { error, primary, success, warning } from "configs/colors";
import { shadows } from "configs/shadows";
import { produce } from "immer";
import { rem } from "utils";

export type MessageServiceType = "Success" | "Warning" | "Error" | "Info";

const MessageIcons: Record<string, React.ElementType> = {
  Success: CheckCircleIcon,
  Warning: AlertTriangleIcon,
  Error: AlertCircleIcon,
  Info: InfoCircleIcon,
};

const MessageColors: Record<string, Record<number, string>> = {
  Success: success,
  Warning: warning,
  Error: error,
  Info: primary,
};

const MessageButtonColors: Record<string, ButtonProps["color"]> = {
  Success: "success",
  Warning: "warning",
  Error: "error",
  Info: "primary",
};

const MessageTitles: Record<string, string> = {
  Success: "Successful",
  Warning: "Something went wrong",
  Error: "Error",
  Info: "Information",
};

export interface MessageServiceOption {
  title?: string;
  content?: React.ReactNode;
  buttonLabel?: string;
  type?: MessageServiceType;
  disableCloseOnBackdropAndEscape?: boolean;
  closeable?: boolean;
  hideIcon?: boolean;
  callback?: () => void;
  callbackClose?: () => void;
}

export enum ActionTypes {
  OPEN,
  CLOSE,
  CLICK,
}

export interface MessageServiceState extends MessageServiceOption {
  open: boolean;
  loading: boolean;
}

export const MessageContext = React.createContext<
  (options: MessageServiceOption) => void
>(() => {});

type ActionType =
  | {
      type: ActionTypes.OPEN;
      payload: MessageServiceOption;
    }
  | {
      type: ActionTypes.CLOSE;
      payload: { loading: boolean };
    }
  | {
      type: ActionTypes.CLICK;
    };

const initialState: MessageServiceState = {
  open: false,
  loading: false,
};

const reducer = produce((draft: MessageServiceState, action: ActionType) => {
  switch (action.type) {
    case ActionTypes.OPEN: {
      draft = initialState;
      draft = { ...draft, ...action.payload };
      draft.open = true;
      draft.loading = false;
      break;
    }
    case ActionTypes.CLOSE: {
      draft.open = false;
      draft.loading = action?.payload?.loading ?? false;
      break;
    }
    default: {
      draft = initialState;
      break;
    }
  }
  return draft;
});

const MessageServiceProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [MessageServiceState, dispatch] = useReducer(reducer, initialState);
  const {
    buttonLabel,
    closeable = true,
    content,
    disableCloseOnBackdropAndEscape,
    open,
    title,
    type = "Info",
    callback,
    callbackClose,
    hideIcon = false,
  } = MessageServiceState;

  const show = useCallback((options: MessageServiceOption) => {
    dispatch({ type: ActionTypes.OPEN, payload: options });
  }, []);

  const close = () => {
    dispatch({ type: ActionTypes.CLOSE, payload: { loading: true } });
    callbackClose?.();
  };

  const click = () => {
    dispatch({ type: ActionTypes.CLOSE, payload: { loading: true } });
    callback?.();
  };

  const color = MessageColors[type as string];

  return (
    <>
      <MessageContext.Provider value={show}>{children}</MessageContext.Provider>
      <Dialog
        open={open}
        onClose={disableCloseOnBackdropAndEscape ? undefined : close}
        PaperProps={{ sx: { minWidth: rem(300) } }}
      >
        <DialogContent>
          <Stack gap={rem(20)} position="relative">
            <Stack
              width="100%"
              alignItems="center"
              justifyContent="center"
              gap={rem(8)}
            >
              {!hideIcon && (
                <Stack
                  alignItems="center"
                  justifyContent="center"
                  borderRadius="50%"
                  width={rem(100)}
                  height={rem(100)}
                  bgcolor={color?.[100]}
                  border={`20px solid ${color?.[50]}`}
                  boxShadow={shadows.xs}
                >
                  <SvgIcon
                    component={MessageIcons[type as string]}
                    sx={{ fill: "none", color: color?.[600] }}
                  />
                </Stack>
              )}

              <Stack gap={rem(4)} alignItems="center">
                <Typography type="TextXL" fontWeight={500} textAlign="center">
                  {title ?? MessageTitles[type as string]}
                </Typography>
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
                {/* @ts-ignore */}
                {!String(content)?.includes("<ul") ? (
                  <Typography type="TextSM" textAlign="center" whiteSpace="pre">
                    {content}
                  </Typography>
                ) : (
                  // eslint-disable-next-line react/no-danger, @typescript-eslint/ban-ts-comment
                  // @ts-ignore
                  // eslint-disable-next-line react/no-danger
                  <div dangerouslySetInnerHTML={{ __html: content }} />
                )}
              </Stack>
            </Stack>
            {buttonLabel ? (
              <>
                <Divider />
                <Button
                  onClick={click}
                  useTypography
                  color={MessageButtonColors[type as string]}
                  loading={MessageServiceState?.loading}
                >
                  {buttonLabel}
                </Button>
              </>
            ) : null}
            {closeable ? (
              <IconButton
                size="small"
                sx={{ position: "absolute", top: "-18px", right: "-22px" }}
                onClick={close}
              >
                <XIcon fontSize="small" />
              </IconButton>
            ) : null}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default MessageServiceProvider;
